import { Component, ChangeDetectionStrategy, inject, input, output } from '@angular/core';
import { trigger, style, animate, transition } from '@angular/animations';
import { translation } from '@cue/translations';
import { ReactiveFormsModule } from '@angular/forms';
import { LoadingComponent } from '@cue/loading';
import { TranslocoPipe } from '@jsverse/transloco';
import { TextBoxModule } from '@progress/kendo-angular-inputs';
import { ExpansionPanelModule } from '@progress/kendo-angular-layout';
import { API_BASEURL, AssistPropertyBM, LocalizedString } from '@cue/api';
import { cueCheck, cueClose, cueArrowRight, IconComponent } from '@cue/ui/icons';
import { NavigationDetailInfo } from '@cue/navigation-services';
import { LocalizedStringPipe } from '@cue/utilities';
import { ButtonComponent } from '@cue/ui';
import { ResourcePoiIdentificationCrate } from '@cue/data';

@Component({
  selector: 'assist-resource-detail',
  templateUrl: './resource-detail.component.html',
  styleUrls: ['./resource-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ExpansionPanelModule,
    TranslocoPipe,
    ReactiveFormsModule,
    LoadingComponent,
    TextBoxModule,
    IconComponent,
    ButtonComponent,
    LocalizedStringPipe,
  ],
  animations: [
    trigger('slideInTop', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-50%)' }),
        animate('300ms', style({ opacity: 1, transform: 'translateY(0%)' })),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'translateY(0%)' }),
        animate('300ms', style({ opacity: 0, transform: 'translateY(-50%)' })),
      ]),
    ]),
    trigger('opacity', [
      transition(':enter', [style({ opacity: 0 }), animate('300ms', style({ opacity: 1 }))]),
      transition(':leave', [style({ opacity: 1 }), animate('300ms', style({ opacity: 0 }))]),
    ]),
    trigger('scale', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0)' }),
        animate('300ms', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'scale(1)' }),
        animate('300ms', style({ opacity: 0, transform: 'scale(0)' })),
      ]),
    ]),
  ],
})
export class ResourceDetailComponent {
  protected apiUrl = inject<string>(API_BASEURL);
  cueCheck = cueCheck;
  cueClose = cueClose;
  cueArrowRight = cueArrowRight;

  translation = translation;
  loading = input<boolean>(false);
  resourceDetail = input<NavigationDetailInfo>();
  requestOpenMapItemDetail = output<ResourcePoiIdentificationCrate>();

  protected openDetail() {
    if (this.resourceDetail().isResource) {
      this.requestOpenMapItemDetail.emit({
        resourceId: this.resourceDetail()?.resourceId,
        qrCodeId: null,
      });
    } else {
      this.requestOpenMapItemDetail.emit({
        resourceId: null,
        qrCodeId: this.resourceDetail()?.qrCodeId,
      });
    }
  }

  getSimpleFiltersByResourceTypeId(detail: NavigationDetailInfo) {
    return this.getFiltersFromNavigationDetail(detail).filter((x) => x.dataTypeId !== 5);
  }

  getEnumerableFiltersByResourceTypeId(detail: NavigationDetailInfo) {
    return this.getFiltersFromNavigationDetail(detail).filter((x) => x.dataTypeId === 5 && x.value?.length > 0);
  }

  private getFiltersFromNavigationDetail(
    detail: NavigationDetailInfo
  ): { name: LocalizedString; value: any; dataTypeId: number; visible: boolean }[] {
    return detail.properties
      .map((property) => ({
        name: property.name,
        visible: property.visible,
        dataTypeId: property.propertyTypeId,
        value: this.getValueFromProperty(property),
      }))
      .filter((x) => x.value);
  }

  getValueFromProperty(property: AssistPropertyBM & { propertyTypeId: number; valueChoices: any[] }) {
    switch (property.propertyTypeId) {
      case 1: {
        return property.valueBoolean;
      }
      case 2: {
        return property.valueNumber;
      }
      case 3: {
        return property.valueDecimal;
      }
      case 4: {
        return property.valueText;
      }
      case 5: {
        return property.valueChoices.map((x) => ({
          title: x.value,
          imageUrl: x.imageUrl,
        }));
      }
      default:
        return null;
    }
  }
}
